import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  Upload, 
  Download, 
  Users, 
  Trash2, 
  Search,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { toast } from 'sonner';
import { API_BASE } from '../../../config/api';
import { useAuth } from '../../../contexts/AuthContext';
import ProgrammeInstructorsPanel from '../../admin/ProgrammeInstructorsPanel';
import ProgrammeCohortsPanel from '../../admin/ProgrammeCohortsPanel';

const API = `${API_BASE}/api`;

export default function LearnersStep({ programmeId, programme, token, headers, onUpdate, onRefreshStatus }) {
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const { isSuperAdmin } = useAuth();

  useEffect(() => {
    if (programmeId) {
      fetchEnrollments();
    } else {
      setLoading(false);
    }
  }, [programmeId]);

  const fetchEnrollments = async () => {
    try {
      const res = await axios.get(`${API}/programmes/${programmeId}/enrollments`, { headers });
      setEnrollments(res.data.enrollments || []);
    } catch (error) {
      console.error('Failed to fetch enrollments:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredEnrollments = enrollments.filter(e => 
    e.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.last_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!programmeId) {
    return (
      <div className="text-center py-12">
        <Users className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-white mb-2">Save Programme First</h3>
        <p className="text-zinc-400">Complete Step 1 to add learners</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-teal-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {isSuperAdmin && <ProgrammeInstructorsPanel programmeId={programmeId} headers={headers} />}
      {isSuperAdmin && <ProgrammeCohortsPanel programmeId={programmeId} headers={headers} />}
      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-zinc-800/50 border-zinc-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <Users className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{enrollments.length}</p>
                <p className="text-sm text-zinc-400">Total Enrolled</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-800/50 border-zinc-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-teal-500/20">
                <CheckCircle className="h-5 w-5 text-teal-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {enrollments.filter(e => e.overall_progress === 100).length}
                </p>
                <p className="text-sm text-zinc-400">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-800/50 border-zinc-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/20">
                <FileSpreadsheet className="h-5 w-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {enrollments.filter(e => e.overall_progress > 0 && e.overall_progress < 100).length}
                </p>
                <p className="text-sm text-zinc-400">In Progress</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-800/50 border-zinc-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/20">
                <AlertCircle className="h-5 w-5 text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {enrollments.filter(e => e.is_at_risk).length}
                </p>
                <p className="text-sm text-zinc-400">At Risk</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upload Section */}
      <div className="flex items-center justify-between">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <Input
            placeholder="Search learners..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-zinc-800 border-zinc-700 text-white"
          />
        </div>
        
        <Button
          onClick={() => navigate(`/admin?tab=enrollments&mode=programmes&programme=${programmeId}`)}
          className="bg-teal-600 hover:bg-teal-700 text-white"
          data-testid="manage-enrolments-link"
        >
          <Users className="h-4 w-4 mr-2" />
          Manage in Enrollments
        </Button>
      </div>

      {/* Enrollments Table */}
      {enrollments.length === 0 ? (
        <div className="text-center py-12 bg-zinc-800/30 rounded-lg border border-dashed border-zinc-700">
          <FileSpreadsheet className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No learners enrolled</h3>
          <p className="text-zinc-400 mb-4">
            Learners are enrolled by a super admin in the Enrollments admin panel
          </p>
        </div>
      ) : (
        <div className="border border-zinc-800 rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 bg-zinc-800/50">
                <TableHead className="text-zinc-400">Name</TableHead>
                <TableHead className="text-zinc-400">Email</TableHead>
                <TableHead className="text-zinc-400">Department</TableHead>
                <TableHead className="text-zinc-400">Progress</TableHead>
                <TableHead className="text-zinc-400">Status</TableHead>
                              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEnrollments.map((enrollment) => (
                <TableRow key={enrollment.enrollment_id} className="border-zinc-800">
                  <TableCell className="text-white font-medium">
                    {enrollment.first_name} {enrollment.last_name}
                  </TableCell>
                  <TableCell className="text-zinc-400">{enrollment.email}</TableCell>
                  <TableCell className="text-zinc-400">{enrollment.department || '-'}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-2 bg-zinc-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-teal-500 rounded-full"
                          style={{ width: `${enrollment.overall_progress || 0}%` }}
                        />
                      </div>
                      <span className="text-sm text-zinc-400">
                        {enrollment.overall_progress || 0}%
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant={enrollment.status === 'active' ? 'default' : 'secondary'}
                        className={enrollment.status === 'active' ? 'bg-teal-500/20 text-teal-400' : 'bg-zinc-700 text-zinc-400'}
                      >
                        {enrollment.status}
                      </Badge>
                      {enrollment.is_at_risk && (
                        <Badge className="bg-red-500/20 text-red-400">At Risk</Badge>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
